import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminSupplier } from '@dukene/shared';
import {
    ArrowLeft,
    Save,
    CheckCircle,
    Loader2,
    Globe,
    Settings,
    Shield,
    Info,
    MapPin,
    Briefcase,
    Zap,
    Key,
    Send,
    Copy,
    AlertTriangle,
} from 'lucide-react';

const INTEGRATION_TYPES = [
    { id: 'CATALOG', label: 'Catalog-Based', description: 'Supplier manages products in Dukene portal' },
    { id: 'API_OAUTH', label: 'API (OAuth 2.0)', description: 'External API with OAuth flow (e.g. LeBrand)' },
    { id: 'API_ONLY', label: 'API (Key)', description: 'External API with static API Keys' },
];

const BUSINESS_TYPES = [
    { id: 'MANUFACTURER', label: 'Manufacturer' },
    { id: 'WHOLESALER', label: 'Wholesaler' },
    { id: 'DISTRIBUTOR', label: 'Distributor' },
    { id: 'RETAILER', label: 'Retailer' },
];

export default function SupplierForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isEdit = !!id;

    const [credentialsModal, setCredentialsModal] = useState<{ apiKey: string; apiSecret: string } | null>(null);

    const { data: supplier, isLoading: isFetching } = useQuery({
        queryKey: ['admin-supplier', id],
        queryFn: () => adminApi.getSupplier(id!),
        enabled: isEdit,
    });

    const [formData, setFormData] = useState<Partial<AdminSupplier>>({
        name: '',
        phoneNumber: '',
        email: '',
        company: '',
        description: '',
        logo: '',
        website: '',
        businessType: 'WHOLESALER' as any,
        integrationType: 'CATALOG' as any,
        isMarketplace: false,
        isVerified: false,
        requiresOAuth: false,
        address: '',
        city: '',
        country: 'Kenya',
        postalCode: '',
        notes: '',
        minOrderValue: 0,
        categories: [],
        tags: [],
        shippingRegions: ['East Africa'],
    });

    useEffect(() => {
        if (supplier) {
            setFormData(supplier);
        }
    }, [supplier]);

    const mutation = useMutation({
        mutationFn: (data: Partial<AdminSupplier>) =>
            isEdit ? adminApi.updateSupplier(id!, data) : adminApi.createSupplier(data),
        onSuccess: (data: any) => {
            queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
            if (!isEdit && data.apiSecret) {
                setCredentialsModal({ apiKey: data.apiKey, apiSecret: data.apiSecret });
            } else {
                navigate('/suppliers');
            }
        },
    });

    const approveMutation = useMutation({
        mutationFn: () => adminApi.approveSupplier(id!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-suppliers', id] });
            queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
            alert('Supplier approved and onboarding email sent.');
        }
    });

    const generateKeysMutation = useMutation({
        mutationFn: () => adminApi.generateSupplierApiKeys(id!),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['admin-suppliers', id] });
            setCredentialsModal({ apiKey: data.apiKey!, apiSecret: data.apiSecret! });
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        mutation.mutate(formData);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev: Partial<AdminSupplier>) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    if (isEdit && isFetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-black" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/suppliers')}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-400 hover:text-black hover:border-black transition-all"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-black text-black">
                            {isEdit ? 'Edit Supplier' : 'Add New Supplier'}
                        </h1>
                        <p className="text-gray-500 text-sm">
                            {isEdit ? `Managing ${formData.name}` : 'Create a new global marketplace supplier'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {isEdit && !formData.isVerified && (
                        <button
                            type="button"
                            onClick={() => approveMutation.mutate()}
                            disabled={approveMutation.isPending}
                            className="inline-flex items-center justify-center h-12 px-6 text-sm font-bold rounded-xl bg-green-500 text-white hover:bg-green-600 transition-all shadow-lg disabled:opacity-50"
                        >
                            {approveMutation.isPending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4 mr-2" />
                            )}
                            Approve & Send Email
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={mutation.isPending}
                        className="inline-flex items-center justify-center h-12 px-8 text-sm font-bold rounded-xl bg-black text-white hover:bg-gray-800 transition-all shadow-lg disabled:opacity-50"
                    >
                        {mutation.isPending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        {isEdit ? 'Update Supplier' : 'Create Supplier'}
                    </button>
                </div>
            </div>

            {/* Credentials Modal */}
            {credentialsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-2 bg-red-500" />
                        <div className="flex items-center gap-3 text-red-500 mb-6">
                            <AlertTriangle className="w-8 h-8" />
                            <h2 className="text-2xl font-black text-black">Save these credentials</h2>
                        </div>
                        <p className="text-gray-500 mb-6">
                            This is the <strong className="text-black">ONLY TIME</strong> the API Secret will be shown. Please copy it now and store it securely. If lost, you will need to regenerate new keys.
                        </p>

                        <div className="space-y-4 mb-8">
                            <div>
                                <label className="text-xs font-black uppercase text-gray-400 tracking-widest block mb-2">API Key</label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm break-all font-mono">
                                        {credentialsModal.apiKey}
                                    </code>
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(credentialsModal.apiKey)}
                                        className="p-4 bg-gray-100 hover:bg-gray-200 rounded-xl text-black transition-colors"
                                    >
                                        <Copy className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-black uppercase text-gray-400 tracking-widest block mb-2">API Secret</label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm break-all font-mono font-bold text-red-600">
                                        {credentialsModal.apiSecret}
                                    </code>
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(credentialsModal.apiSecret)}
                                        className="p-4 bg-gray-100 hover:bg-gray-200 rounded-xl text-black transition-colors"
                                    >
                                        <Copy className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setCredentialsModal(null);
                                navigate('/suppliers');
                            }}
                            className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                        >
                            I have saved the secret
                        </button>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Info Section */}
                <section className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-2 mb-8">
                        <Info className="w-5 h-5 text-black" />
                        <h2 className="text-xl font-black text-black">Basic Information</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Supplier Name *</label>
                            <input
                                type="text"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="e.g. Global Tech Distribution"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Phone Number *</label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                required
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="+254700000000"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email || ''}
                                onChange={handleChange}
                                placeholder="contact@supplier.com"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Company Name</label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company || ''}
                                onChange={handleChange}
                                placeholder="Business Legal Name"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Brief Description</label>
                            <textarea
                                name="description"
                                rows={3}
                                value={formData.description || ''}
                                onChange={handleChange}
                                placeholder="Describe what this supplier offers..."
                                className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all resize-none"
                            />
                        </div>
                    </div>
                </section>

                {/* Marketplace & Verification */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <section className="bg-black text-white border border-gray-800 rounded-3xl p-8 shadow-xl">
                        <div className="flex items-center gap-2 mb-6">
                            <Globe className="w-5 h-5 text-indigo-400" />
                            <h2 className="text-xl font-black">Marketplace Status</h2>
                        </div>
                        <div className="space-y-6">
                            <label className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                                <input
                                    type="checkbox"
                                    name="isMarketplace"
                                    checked={formData.isMarketplace}
                                    onChange={handleChange}
                                    className="w-6 h-6 rounded-lg bg-white/10 border-white/20 text-indigo-500 focus:ring-indigo-500"
                                />
                                <div>
                                    <p className="font-bold">List in Global Marketplace</p>
                                    <p className="text-xs text-gray-400">Make this supplier visible to all merchants</p>
                                </div>
                            </label>

                            <label className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                                <input
                                    type="checkbox"
                                    name="isVerified"
                                    checked={formData.isVerified}
                                    onChange={handleChange}
                                    className="w-6 h-6 rounded-lg bg-white/10 border-white/20 text-green-500 focus:ring-green-500"
                                />
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <p className="font-bold">Verified Supplier</p>
                                        <Shield className="w-3 h-3 text-green-400" />
                                    </div>
                                    <p className="text-xs text-gray-400">Display a verification badge and boost ranking</p>
                                </div>
                            </label>
                        </div>
                    </section>

                    <section className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <Briefcase className="w-5 h-5 text-black" />
                            <h2 className="text-xl font-black text-black">Business Type</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {BUSINESS_TYPES.map((type) => (
                                <button
                                    key={type.id}
                                    type="button"
                                    onClick={() => setFormData((p: Partial<AdminSupplier>) => ({ ...p, businessType: type.id as any }))}
                                    className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-all ${formData.businessType === type.id
                                        ? 'border-black bg-black text-white'
                                        : 'border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200'
                                        }`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Integration Settings */}
                <section className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-2 mb-8">
                        <Zap className="w-5 h-5 text-black" />
                        <h2 className="text-xl font-black text-black">Integration Type</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {INTEGRATION_TYPES.map((type) => (
                            <label
                                key={type.id}
                                className={`relative flex flex-col p-6 rounded-2xl border-2 cursor-pointer transition-all ${formData.integrationType === type.id
                                    ? 'border-black bg-black text-white'
                                    : 'border-gray-100 bg-gray-50 text-black hover:border-gray-200'
                                    }`}
                            >
                                <input
                                    type="radio"
                                    name="integrationType"
                                    value={type.id}
                                    checked={formData.integrationType === type.id}
                                    onChange={handleChange}
                                    className="sr-only"
                                />
                                <span className="font-black text-lg mb-2">{type.label}</span>
                                <span className={`text-xs ${formData.integrationType === type.id ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {type.description}
                                </span>
                                {formData.integrationType === type.id && (
                                    <CheckCircle className="absolute top-4 right-4 w-5 h-5" />
                                )}
                            </label>
                        ))}
                    </div>

                    {formData.integrationType === 'API_OAUTH' && (
                        <div className="mt-12 space-y-6 pt-10 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-500">
                            <div className="flex items-center gap-2 mb-4">
                                <Settings className="w-4 h-4 text-gray-400" />
                                <h3 className="text-sm font-black uppercase text-gray-400 tracking-widest">OAuth 2.0 Configuration</h3>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Client ID</label>
                                    <input
                                        type="text"
                                        name="oauthClientId"
                                        value={formData.oauthClientId || ''}
                                        onChange={handleChange}
                                        className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none text-sm transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Client Secret</label>
                                    <input
                                        type="password"
                                        name="oauthClientSecret"
                                        value={formData.oauthClientSecret || ''}
                                        onChange={handleChange}
                                        className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none text-sm transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Authorization URL</label>
                                    <input
                                        type="url"
                                        name="oauthAuthorizeUrl"
                                        value={formData.oauthAuthorizeUrl || ''}
                                        onChange={handleChange}
                                        className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none text-sm transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Token URL</label>
                                    <input
                                        type="url"
                                        name="oauthTokenUrl"
                                        value={formData.oauthTokenUrl || ''}
                                        onChange={handleChange}
                                        className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none text-sm transition-all"
                                    />
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Scopes (comma separated)</label>
                                    <input
                                        type="text"
                                        name="oauthScopes"
                                        value={Array.isArray(formData.oauthScopes) ? formData.oauthScopes.join(', ') : formData.oauthScopes || ''}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                oauthScopes: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            }));
                                        }}
                                        placeholder="read_orders, write_products"
                                        className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none text-sm transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {formData.integrationType === 'API_ONLY' && isEdit && (
                        <div className="mt-12 space-y-6 pt-10 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-500">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Key className="w-4 h-4 text-gray-400" />
                                    <h3 className="text-sm font-black uppercase text-gray-400 tracking-widest">API Keys</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => generateKeysMutation.mutate()}
                                    disabled={generateKeysMutation.isPending}
                                    className="text-sm font-bold text-red-500 hover:text-red-700 transition-colors"
                                >
                                    {generateKeysMutation.isPending ? 'Regenerating...' : 'Regenerate Keys'}
                                </button>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest">API Key Prefix</label>
                                <div className="w-full h-11 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 flex items-center text-sm text-gray-500 font-mono">
                                    {formData.apiKey ? `${formData.apiKey.substring(0, 20)}...` : 'Not generated'}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">API Secret is hashed and cannot be shown. Regenerate keys to issue a new secret.</p>
                            </div>
                        </div>
                    )}
                </section>

                {/* Location Section */}
                <section className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-2 mb-8">
                        <MapPin className="w-5 h-5 text-black" />
                        <h2 className="text-xl font-black text-black">Location & Address</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2 space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Street Address</label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address || ''}
                                onChange={handleChange}
                                placeholder="123 Business Park, Mombasa Road"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">City</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city || ''}
                                onChange={handleChange}
                                placeholder="Nairobi"
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase text-gray-400 tracking-widest">Country</label>
                            <input
                                type="text"
                                name="country"
                                value={formData.country || ''}
                                onChange={handleChange}
                                className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                            />
                        </div>
                    </div>
                </section>
            </form>
        </div>
    );
}
